import {
  Component, Prop, Vue, Watch,
} from 'vue-property-decorator';
import { Tabbar, TabbarItem } from '@hb/finance-ui';
import TabConfigs from '@/shared/utils/tabConfigs';
import '@/views_v2/home/store';
import { namespace } from 'vuex-class';
import type { AutarkyStateType } from '@/api/homeV2Status/types';
import { RouterV2Enum } from '@/router/router-names';
import { session } from '@/shared/utils/storage';
import {
  SuperMemberSourceEnum, RightPackageTypeEnum, TabStyleTypeEnum, PackageBuyStatusEnum,
} from '@/api/rights/types';
import { NavIntentEnums } from '@/shared/utils/tabConfigs/types';
import { RightHelper } from '@/shared/right-router-helper';
import { openThirdPage } from '@/api/native/utils';
import { getImgWidth } from '@/shared/utils/cssComputed';
import { skNativeContainer } from '@/api/native/container';
import isPrerendering from '@/shared/utils/isPrerendering';
import type { TabItemType } from './types';

import '../components.theme.blue.scss';
import style from './index.module.scss';

const HomeStore = namespace('home');
const BaseStore = namespace('base');

@Component({ name: 'HomeBottomBar' })
export default class HomeBottomBar extends Vue {
  @HomeStore.State autarkyState!: AutarkyStateType| undefined;

  @BaseStore.State tabInfoList!: TabItemType[] | null;

  @BaseStore.Action getMemberTabInfo!: ()=> Promise<TabItemType[] | null>;

  @BaseStore.State isHybridAppMode!: boolean;

  @Prop()
  private currentPage!: string

  private active = 0;

  private async created() {
    this.showNativeTabbar = await skNativeContainer();
    if (this.showNativeTabbar) {
      /** 显示tabbar时，不调用权益tab接口 */
      return;
    }

    /**
     * 如果是预渲染的，不执行接口调用，防止接口异常渲染出登录页
     */
    if (!isPrerendering()) {
      // store中不存在权益tab数据时再调接口获取，防止频繁切换时，tabbar抖动
      if (!this.tabInfoList
        || !this.tabInfoList.length) {
        await this.getMemberTabInfo();
        // 如果有权益tab，上报埋点
        if (this.tabInfoList?.filter(
              (item) => item.id === NavIntentEnums.financeMember
            )?.length) {
          this.$ubtModuleExpose('finance_loan_tab_member');
        }
      }

      RightHelper.initBuyStatus(RightPackageTypeEnum.SUPER_MEMBER_CARD);
    }
  }

  @Watch('active')
  private async onActiveTabChange(active: number, lastActive: number) {
    this.showNativeTabbar = await skNativeContainer();
    if (this.showNativeTabbar) {
      /** 显示tabbar时，不上报底部h5tabbar点击埋点 */
      return;
    }
    if (lastActive !== -1) {
      this.$ubtPageButton(
        TabConfigs[active].buttonName,
        '当期页面',
        TabConfigs[lastActive].buttonName,
        'currentPage',
        this.currentPage,
      );
    }
  }

  private isActive(tabName: string): boolean {
    return TabConfigs[this.active].name === tabName;
  }

  private showNativeTabbar: boolean | null = null;


  private render() {
    /** 客户端tabbar显示时 隐藏h5tabbar
     * 从钱包页tab过来的时，不显示h5tabbar
     */
    if (this.showNativeTabbar || this.isHybridAppMode) {
      return null;
    }
    return (
      <Tabbar
        activeIndex={this.active}
        onChange={this.onChangePath.bind(this)}
      >
        {this.tabInfoList
        && this.tabInfoList.map((entry: TabItemType) => (
          <TabbarItem
            key={entry.id}
            name={entry.name}
            class={[
              style.tabName,
              this.isActive(entry.name) && style.activedTabItem,
            ]}
          >
            <span
              icon={entry.icon}
              class={[
                style.icon,
                this.isActive(entry.name) && style.activeIcon,
              ]}
            >
              {entry.badgeType && entry.badgeContent && (
                entry.badgeType === TabStyleTypeEnum.TEXT_ICON
                  ? (<i class={style.tabberBadge}>{entry.badgeContent}</i>)
                  : (<img class={style.badgeImg} src={entry.badgeContent} style={{ width: `${getImgWidth(entry.badgeContent)}` }}/>))
              }
            </span>
          </TabbarItem>
        ))}
      </Tabbar>
    );
  }

  private mounted() {
    const activeIndex = TabConfigs.findIndex((val) => val.path === this.$route.path);

    if (activeIndex !== -1) {
      this.active = activeIndex;
    }
  }

  private onChangePath(active: number) {
    const {
      path, clickMouduleId, id, extendInfo,
    } = TabConfigs[active];
    this.$ubtPageButton(clickMouduleId);

    // 如果点击位为会员tab
    if (id === NavIntentEnums.financeMember) {
      // 因用户购买前后页面展示差异巨大，这里根据用户购买状态获取分发的会员主页面预渲染地址，用于提高用户体验
      const url = RightHelper.dispatchRouterByBuyStatus(
        extendInfo?.packageType as RightPackageTypeEnum,
        SuperMemberSourceEnum.FINANCE_MEMBER_TAB,
        extendInfo?.packageBuyStatus as PackageBuyStatusEnum,
      );

      // 如果是从主tab进入，开启页面监听
      // TODO: 因臻有钱回退的方法存在问题，待后续修改后再重新调整上线
      // startMonitorPageVisibility();
      openThirdPage(url);
      return;
    }

    // 跳转其他tab存储的首页path
    const homeTabPath = session.get('homeTabPath');
    let changePath = path;

    // 如果跳转页面不是首页并且session中没有homeRoutePath
    if (path !== RouterV2Enum.home && !homeTabPath) {
      session.set('homeTabPath', this.$route.path);
    }
    if (path === RouterV2Enum.home && homeTabPath) {
      changePath = homeTabPath as RouterV2Enum;
    }

    this.active = active;
    this.$router.replace({
      path: changePath,
    });
  }
}
